// src/api/auth/forgetPassword/useSecurityQuestionCheck.ts

import { useMutation } from "@tanstack/react-query";

import { useNavigate, useParams } from "react-router-dom";
import { Methods } from "../../../Constants/ApiConstants";
import { EndPoints } from "../../../Constants/endpoints";
import {
  GeneralResponse,
  RegisterResponse,
} from "../../../interface/GeneralResponse";
import { useToast } from "../../../Services/ToastContext";
import Api from "../../generalApi/Api";
import { ApiError } from "../../generalApi/ApiError";
import { useTranslation } from "react-i18next";

interface SecurityQuestionsCheckRequestData {
  Id: number;
  FullName: string;
  SpecialtyId: number;
  AccountTypeId: number;
}

const useSecurityQuestionCheck = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { setting } = useParams();

  const {t} = useTranslation()
  const mutation = useMutation<
    GeneralResponse<RegisterResponse>,
    ApiError,
    SecurityQuestionsCheckRequestData
  >({
    mutationFn: async (data: SecurityQuestionsCheckRequestData) => {
      if (!data || Object.keys(data).length === 0) {
        throw new ApiError("Invalid data", 400, 400);
      }

      const response = await Api<
        SecurityQuestionsCheckRequestData,
        GeneralResponse<RegisterResponse>
      >({
        method: Methods.POST,
        endpoint: EndPoints.SecurityQuestionCheck,
        body: data,
      });

      // If the API returns Status = false, we can manually throw an error
      // to ensure that we handle error scenarios in onError.
      if (!response.Status) {
        throw new ApiError(
          response.ErrorMessage || "Unknown error",
          response.Code,
          response.Code,
        );
      }

      return response;
    },
    onSuccess: (response) => {
      // If we're here, it means response.Status = true.
      // Navigate to reset-password page on success.
      if (response && response.Status) {
        navigate(
          setting === "setting" ? "/reset-password/setting" : "/reset-password",
        );
      }
    },
    onError: (error: ApiError) => {
      // Handle error scenarios, including blocked user.
      if (error.code === 405) {
        // User is blocked
        navigate("/user-blocked");
        if (error.message) {
          showToast(error.message, "error");
        } else {
          showToast(t("account_blocked"), "error");
        }
      } else {
        // Other errors
        const errorMessage = error.message || t("error_occurred");
        showToast(errorMessage, "error");
      }
    },
  });

  return mutation;
};

export default useSecurityQuestionCheck;
